<template>
  <v-expansion-panel-content>
    <ColorDialog
      title="Select trim"
      intro="To see fewer fabric choices, use the filter.<br>(Depending on your screen, you may need to scroll the menu to see all available fabrics.)"
      ref="trim"
      :categories="sortedTrims"
      @set-color="setColor"
      :is-utv="isUTV"
      sb
    />
    <ColorDialog
      title="Select insert"
      intro="To see fewer fabric choices, use the filter.<br>(Depending on your screen, you may need to scroll the menu to see all available fabrics.)"
      ref="insert"
      :categories="sortedInserts"
      @set-color="setColor"
      :is-utv="isUTV"
      exoticMessage
    />
    <QuiltDialog
      ref="quilt"
      @set-quilt="setQuilt"
    />
    <Navigate previous />
    <v-row v-for="position in positions" :key="position">
      <template v-if="variations[position]">
        <v-col cols="12" sm="6">
          <ModelInfo v-if="position === 'front'" />
          <v-row>
            <v-col cols="12">
              <h6>{{capitalize(position)}} Seat Details</h6>
            </v-col>
          </v-row>
          <v-row>
            <v-col lg="3">
              <SeatDrawing :seat="variations[position]" />
            </v-col>
            <v-col lg="9" class="seat-padd">
              <p>{{variations[position].svDescription}}</p>
            </v-col>
          </v-row>
          <div v-if="variations[position].trim || variations[position].insert">
            <span class="clear-sel" @click="clearMaterials(position)">
              [ x ] clear selections
            </span>
          </div>
          <div v-if="position !== 'front'">
            <v-btn small text class="copy-btn" @click="copyMaterial(position)">
              <v-icon>far fa-copy</v-icon>
              Copy Front Seat Selections
            </v-btn>
            <br />
            <span class="option-text">- or -</span>
          </div>
          <v-row>
            <v-col>
              <p class="sb"><small>For Saddle Blanket do not choose a trim fabric. Choose an insert fabric only. Your choice will apply to the entire seat cover.</small></p>
            </v-col>
          </v-row>
          <v-row>
            <v-col v-for="(c, i) in cuts" :key="i" cols="12" sm="6" md="4" lg="3">
              <v-btn
                block
                color="primary"
                class="mb-4"
                @click.stop="openColorDialog(position, c)"
              >
                Choose {{c}}
              </v-btn>
              <v-row class="d-flex justify-left">
                <Swatch :color="variations[position][c]" withName />
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="hasDiamondQuilt(variations[position])">
            <v-col cols="12" sm="6" md="6" lg="4">
              <v-btn
                block
                color="primary"
                class="mb-4"
                @click.stop="openQuiltDialog(position)"
              >
                Choose Quilt Option
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-if="hasDiamondQuilt(variations[position])">
            <SwatchQuilt :position="position" withName />
          </v-row>
        </v-col>
        <v-col cols="12" sm="6">
          <small v-if="position === 'front'">Seat images are for convenience in selecting fabrics and colors. They may not exactly match the vehicle's seats. Colors may appear differently on different screens. The color stripe in our Saddle Blanket fabric varies in width by fabric lot and from color to color. Please contact Ruff Tuff with questions. Fabric samples are available on request.<br><br>Covers for armrests and adjustable headrests are sold separately. Select them when choosing options.</small>
          <SeatPreview :seat="variations[position]" />
        </v-col>
      </template>
    </v-row>
    <Total />
    <Navigate previous next :valid="validateSeats()" />
  </v-expansion-panel-content>
</template>

<style scoped lang="scss">
.total {
  font-size: 24px;
}
.total h4 {
  margin-right: 8px;
}
h6 {
  font-family: $body-font-family !important;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 0;
}
p {
  font-size: 15px;
  font-weight: 300;
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;

  @media screen and (min-width: 600px) {
    max-width: 380px;
  }
}

.seat-padd {
  padding: 20px 0;
}

.v-btn__content .v-icon {
  margin-right: 8px
  }


.top-nav {
  margin-top: 20px;
}
.copy-btn {
  margin: 15px 0;
  padding: 10px !important;
  height: auto !important;
  &::before {
    opacity: 0.04;
  }

  i {
    font-weight: 300;
    font-size: 20px;
  }
}
.option-text {
  font-size: 13px;
  padding-left: 16px;
}
.clear-sel {
  font-size: 14px;
  font-weight: 300;
  cursor: pointer;
}
.sb {
  margin: 0 !important;
  color: $brand-primary;
  font-weight: 700;
}
</style>

<script>
import { mapState, mapGetters } from 'vuex'
import { capitalize, getDiamondQuiltIndex, getDiamondQuiltOption, positions, sortBy } from '@/utils'
import { getTile } from '@/services/assets'
import Materials from '@/services/materials'
import Threads from '@/services/threads'
import ModelInfo from '@/components/ModelInfo'
import ColorDialog from '@/components/ColorDialog'
import QuiltDialog from '@/components/QuiltDialog'
import SeatPreview from '@/components/SeatPreview'
import Swatch from '@/components/Swatch'
import SwatchQuilt from '@/components/SwatchQuilt'
import Total from '@/components/Total'
import Navigate from '@/components/Navigate'
import SeatDrawing from './SeatDrawing'

export default {
  components: {
    ModelInfo,
    ColorDialog,
    QuiltDialog,
    SeatPreview,
    Swatch,
    SwatchQuilt,
    Total,
    Navigate,
    SeatDrawing
  },
  data () {
    return {
      cuts: ['trim', 'insert'],
      trims: {},
      inserts: {},
      threads: {},
      row: null,
      cut: null,
    }
  },
  mounted () {
    this.getMaterials()
  },
  computed: {
    ...mapState([
      'year',
      'make',
      'model',
      'pricing',
      'attributes',
      'variations'
    ]),
    ...mapGetters([
      'isUTV',
      'priceOption',
    ]),
    positions,
    sortedTrims: function() {
      return Object
        .values(this.trims)
        .sort(sortBy('productCode'))
        .filter(this.filterUTV)
    },
    sortedInserts: function() {
      return Object
        .values(this.inserts)
        .sort(sortBy('productCode'))
        .filter(this.filterUTV)
    },
  },
  methods: {
    capitalize: capitalize,
    getTile: getTile,
    openColorDialog(row, cut) {
      this.row = row
      this.cut = cut
      this.$refs[cut].open()
    },
    openQuiltDialog(row) {
      this.row = row
      this.$refs.quilt.open()
    },
    setColor(color) {
      const variation = this.variations[this.row]
      if (variation[this.cut]?.materialProductCode === 'saddleblanket') {
        delete variation.trim
        delete variation.insert
      }
      if (color.materialProductCode === 'saddleblanket') {
        variation.trim = variation.insert = color
      } else {
        variation[this.cut] = color
      }
      this.$store.commit('setVariations', { row: this.row, ...variation })
    },
    setQuilt(style, color) {
      const position = this.row
      const variation = this.variations[position]

      // Clear previous selection if needed
      if (variation.selectedQuiltIndex !== undefined) {
        this.$store.commit('addOption', {
          position,
          index: variation.selectedQuiltIndex,
          added: false,
          style: undefined,
          color: undefined
        })
      }

      // Get the new quilt option index based on style.part
      const index = getDiamondQuiltIndex(variation, style)
      variation.selectedQuiltIndex = index

      // For Diamond (no color), mark added = true
      const added = style.name.toLowerCase() === 'diamond' ? true : !!color

      this.$store.commit('addOption', {
        position,
        index,
        added,
        style,
        color
      })
    },
    clearMaterials(row) {
      const variation = this.variations[row]
      delete variation.trim
      delete variation.insert
      this.$store.commit('setVariations', { row, ...variation })
    },
    copyMaterial(row) {
      this.copyCut(this.variations.front, this.variations[row])
      this.copyQuilt(this.variations.front, this.variations[row])
      this.$store.commit('setVariations', { row, ...this.variations[row] })
    },
    filterUTV(category) {
      return !this.isUTV || (this.isUTV && category.hasUTV)
    },
    copyCut(master, clone) {
      this.cuts.forEach((cut) => {
        clone[cut] = Object.assign({}, master[cut])
      })
    },
    copyQuilt(master, clone) {
      const quilt = master.options[getDiamondQuiltIndex(master)]
      clone.options[getDiamondQuiltIndex(clone)] = Object.assign({}, quilt)
    },
    getDiamondQuiltPrice(variation) {
      return this.pricing ? `($${getDiamondQuiltOption(variation)[this.priceOption(this.pricing)]})` : ''
    },
    getDiamondQuiltIndex(variation) {
      return getDiamondQuiltIndex(variation)
    },
    hasDiamondQuilt(variation) {
      if (!variation || !variation.insert) {
        return false
      }

      return variation.DiamondQuilting && variation.insert.diamondQuilting
    },
    validateSeats() {
      return Object.values(this.variations).every((seat) => {
        return seat.trim && seat.insert
      })
    },
    async getMaterials() {
      this.trims = await Materials.trims()
      this.inserts = await Materials.inserts()
    },
    async getThreads() {
      this.threads = await Threads.get()
    }
  }
}
</script>
